// „Als Vorlage verwenden": aus einem bestehenden Protokoll einen neuen lokalen Entwurf.
//
// Der Entwurf muss LOKAL entstehen (IndexedDB), nicht in der Datenbank: der Assistent
// arbeitet offline-fähig auf `LokalerEntwurf` und lädt die Fotos erst beim Abschluss
// hoch. Deshalb werden die Bilder der Vorlage hier aus dem Bucket geholt und als Blobs
// unter den neuen Pfaden abgelegt — so hängt das neue Protokoll an eigenen Dateien und
// überlebt es, wenn die Vorlage später aufgeräumt wird.
import { supabase } from '../supabase/client';
import { kopiereProtokoll, medienPfade, schreibePfadeUm } from './kopieren';
import { speichereEntwurf, type LokalerEntwurf } from './lokal';
import { ladeKontakteFuerBezug, verteileRollen, type Bezug } from './bezug';
import type { Art, Protokoll } from './typen';

const BUCKET = 'uebergaben';

export interface VorlageErgebnis {
  entwurfId: string;
  /** Fotos, die sich nicht laden ließen — die Oberfläche nennt sie, statt sie zu verschweigen. */
  fehlendeBilder: number;
}

/**
 * @param ziel  Optional (Ticket #436, Hausmeister): den Entwurf nicht unter neuer Id anlegen,
 *              sondern für ein BESTEHENDES Protokoll — das Büro hat die Kopfzeile schon angelegt
 *              und freigegeben. Objekt, Einheit, Mieter, Eigentümer und Art kommen dann aus
 *              dieser Kopfzeile, nicht aus der Vorlage; nur Räume, Zähler und Schlüssel stammen
 *              aus der Vorlage.
 */
export async function erzeugeAusVorlage(
  vorlageId: string,
  art: Art,
  userProfile: { id?: string | null; full_name?: string | null; email?: string | null } | null | undefined,
  ziel?: { protokollId: string; bezug: Bezug },
): Promise<VorlageErgebnis> {
  const { data, error } = await supabase
    .from('uebergabeprotokolle').select('*').eq('id', vorlageId).single();
  if (error || !data) throw new Error(error?.message || 'Vorlage nicht gefunden.');

  const vorlage = data as unknown as Protokoll;
  const neueId = ziel?.protokollId ?? crypto.randomUUID();
  let protokoll = kopiereProtokoll(vorlage, { neueId, art: ziel?.bezug.art ?? art, erfasstVon: userProfile?.id ?? null });
  if (ziel) {
    const { bezug } = ziel;
    // Parteien wie bei einer Neuanlage aus dem Bezug; ohne Netz ohne vorbelegte Namen —
    // dieselbe Rückfallebene wie im Assistenten.
    let parteien = protokoll.parteien;
    try {
      parteien = verteileRollen(bezug, await ladeKontakteFuerBezug(bezug), {
        name: userProfile?.full_name ?? '', email: userProfile?.email ?? null,
      });
    } catch { /* ohne Kontakte weiter */ }
    protokoll = {
      ...protokoll,
      objekt_id: bezug.objekt_id, einheit_id: bezug.einheit_id,
      mieter_id: bezug.mieter_id, eigentuemer_id: bezug.eigentuemer_id,
      parteien,
    };
  }

  // Bilder der Vorlage holen. Einzelne Fehlschläge dürfen das Kopieren nicht verhindern —
  // ein Protokoll ohne ein Foto ist brauchbar, ein abgebrochener Vorgang nicht.
  const fotos: Record<string, Blob> = {};
  let fehlendeBilder = 0;
  for (const alterPfad of medienPfade(vorlage)) {
    const { data: blob } = await supabase.storage.from(BUCKET).download(alterPfad);
    if (blob) fotos[schreibePfadeUm(alterPfad, vorlage.id, neueId)] = blob;
    else fehlendeBilder += 1;
  }

  const entwurf: LokalerEntwurf = {
    id: neueId,
    protokoll: entferneFehlendeFotos(protokoll, fotos),
    fotos,
    unterschriften: {},
    unterschrift_entwuerfe: {},
    gespeichert_am: new Date().toISOString(),
  };
  await speichereEntwurf(entwurf);
  return { entwurfId: neueId, fehlendeBilder };
}

/** Pfade ohne geladenen Blob entfernen — sonst zeigt der Assistent Bilder an, die es nicht gibt. */
function entferneFehlendeFotos(p: Protokoll, fotos: Record<string, Blob>): Protokoll {
  const da = (pfad: string) => !!fotos[pfad];
  return {
    ...p,
    raeume: (p.raeume || []).map((r) => ({
      ...r,
      fotos: (r.fotos || []).filter(da),
      pruefpunkte: (r.pruefpunkte || []).map((pp) => ({ ...pp, fotos: (pp.fotos || []).filter(da) })),
    })),
    zaehler: (p.zaehler || []).map((z) => ({ ...z, foto: z.foto && da(z.foto) ? z.foto : null })),
  };
}
